/**
 * The writing score on My stats, and its weekly heatmap (GitHub style, one cell per week, last 52 weeks).
 * Each week has a score from 0 to 100 from the words written, the time in the editor, revisions and how many
 * documents were worked on (the Otable algorithm, registry/user/writing_score.py; its weights are not
 * shown, and the API does not send them); the rows show the
 * score of proposals, publications, reflections and posts (posts weigh less), the top row the week as a whole. Cells use one hue,
 * light→dark, on a fixed scale (1–20, 21–40 … 81–100), so a dark week means the same in every row;
 * weeks without writing are neutral grey. Hover a cell for its week; a hidden table carries the numbers.
 */
import React, { useState } from 'react';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

// Ordinal ramp on the app's indigo, checked with the dataviz validator (monotone L, ΔL ≥ 0.06,
// lightest step 2.2:1 against the page). Index 0 = level 1.
export const RAMP = ['#9aa6f9', '#7c86f4', '#6366f1', '#4f46e5', '#3730a3'];
export const ROWS = [['proposal', 'Proposals'], ['publication', 'Publications'], ['reflections', 'Reflections'], ['posts', 'Posts']];
export const wrote = (w) => w.score || 0;
/** A week's score (0–100) on the fixed five-step scale; 0 = no writing. */
export const level = (v) => (v > 0 ? Math.min(5, Math.ceil(v / 20)) : 0);
export const fill = (v) => (v > 0 ? RAMP[level(v) - 1] : undefined);

export const fmtWeek = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const hm = (min) => (min >= 60 ? `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}` : `${min} min`);
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** What one week was made of, in words. */
export function weekDetail(w) {
  if (!w.score) return 'no writing';
  return [plural(w.words, 'word'), `${hm(w.minutes)} writing`, plural(w.revisions, 'revision'),
    plural(w.documents, 'document')].join(' · ');
}

export function streak(weeks) {
  // weeks in a row with any writing, ending this week (or last week if this one hasn't started yet)
  let i = weeks.length - 1;
  if (weeks[i] && !wrote(weeks[i])) i -= 1;
  let n = 0;
  while (i >= 0 && wrote(weeks[i])) { n += 1; i -= 1; }
  return n;
}

/** The score and what it is made of over the last 12 weeks. */
export function ScoreBreakdown({ summary, compact = false }) {
  if (!summary) return null;
  const trend = summary.previous == null ? null : summary.score - summary.previous;
  return (
    <Box>
      <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
        <Typography sx={{ fontSize: compact ? 30 : 36, fontWeight: 800, lineHeight: 1 }}>{summary.score}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>writing score · last 12 weeks</Typography>
        {trend != null && trend !== 0 && (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: trend > 0 ? 'success.main' : 'text.secondary' }}>
            {trend > 0 ? '▲' : '▼'} {Math.abs(trend)} on the 12 weeks before
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 1, mt: 1.5 }}>
        {summary.components.map((c) => (
          <Box key={c.key}>
            <Box display="flex" justifyContent="space-between">
              <Typography sx={{ fontSize: 12.5 }}>{c.label}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {c.key === 'minutes' ? hm(Math.round(c.per_week)) : (c.key === 'words' ? Math.round(c.per_week) : c.per_week).toLocaleString()} a week
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={c.percent} aria-label={c.label}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: RAMP[3], borderRadius: 3 } }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function WritingHeatmap({ weeks = [], summary = null, isOwn = true, action = null }) {
  const [hover, setHover] = useState(null);           // { row: -1 (all) | index, i }
  if (!weeks.length) return null;

  const activeWeeks = weeks.filter(wrote).length;
  const run = streak(weeks);
  let lastLabel = -9;
  const months = weeks.map((w, i) => {
    const d = new Date(`${w.week_start}T00:00:00`);
    const prev = i ? new Date(`${weeks[i - 1].week_start}T00:00:00`) : null;
    if ((prev && prev.getMonth() === d.getMonth()) || i - lastLabel < 3) return '';   // no crowded labels
    lastLabel = i;
    return d.toLocaleDateString(undefined, { month: 'short' });
  });
  const grid = { display: 'grid', gridTemplateColumns: `84px repeat(${weeks.length}, minmax(0, 1fr))`, columnGap: '2px', alignItems: 'center' };
  const cell = (row, i, v, height) => (
    <Box key={weeks[i].week_start} onMouseEnter={() => setHover({ row, i })} onMouseLeave={() => setHover(null)}
      sx={{ ...(height ? { height } : { aspectRatio: '1', maxHeight: 16 }), borderRadius: height ? '3px' : '2px',
        bgcolor: fill(v) || 'action.hover',
        outline: hover && hover.row === row && hover.i === i ? '2px solid' : 'none', outlineColor: 'text.primary', outlineOffset: '1px' }} />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mt: 2 }}>
      <Box display="flex" alignItems="flex-start">
        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>{isOwn ? 'Your writing, week by week' : 'Writing, week by week'}</Typography>
        {action}
      </Box>
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.25, mb: 2 }}>
        {activeWeeks
          ? `Writing in ${activeWeeks} of ${weeks.length} weeks` + (run > 1 ? ` · ${run} weeks in a row now` : run === 1 ? ' · writing this week' : '')
          : isOwn ? 'Writing in a proposal, a publication, a reflection or a post fills a square here. Your first one is a click away.'
            : 'No writing yet.'}
      </Typography>

      {summary && <Box sx={{ mb: 2.5 }}><ScoreBreakdown summary={summary} /></Box>}

      <Box sx={{ position: 'relative', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 560 }}>
          <Box sx={{ ...grid, mb: 0.5 }}>
            <span />
            {months.map((m, i) => (
              <Typography key={weeks[i].week_start} sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'visible', width: 0 }}>{m}</Typography>
            ))}
          </Box>
          {/* All writing: the week's score, in taller cells above the rows by kind (same columns) */}
          <Box sx={{ ...grid, mb: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, pr: 1 }}>All writing</Typography>
            {weeks.map((w, i) => cell(-1, i, wrote(w), 26))}
          </Box>
          {ROWS.map(([key, label], row) => (
            <Box key={key} sx={{ ...grid, mb: '2px' }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', pr: 1 }}>{label}</Typography>
              {weeks.map((w, i) => cell(row, i, w[key] || 0))}
            </Box>
          ))}
          <Box display="flex" alignItems="center" gap={0.5} mt={1}>
            <Typography sx={{ fontSize: 12.5, flex: 1, color: hover ? 'text.primary' : 'text.secondary' }} aria-live="polite">
              {(() => {
                if (!hover) return isOwn ? 'Hover a week to see what you wrote.' : 'Hover a week to see its writing.';
                const w = weeks[hover.i];
                if (hover.row === -1) return `Week of ${fmtWeek(w.week_start)} · score ${w.score} · ${weekDetail(w)}`;
                const [key, label] = ROWS[hover.row];
                return `Week of ${fmtWeek(w.week_start)} · ${label}: ${w[key] ? `score ${w[key]}` : 'no writing'}`;
              })()}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mr: 0.5 }}>0</Typography>
            {[null, ...RAMP].map((c, i) => (
              <Box key={c || 'none'} aria-hidden sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: c || 'action.hover' }}
                title={i ? `score ${i * 20 - 19}–${i * 20}` : 'no writing'} />
            ))}
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.5 }}>100</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={visuallyHidden}>{/* data for screen readers; a div, because a table can't be squeezed to 1px */}
        <table>
          <caption>Writing score per week, last 52 weeks</caption>
          <thead><tr><th>Week of</th><th>Score</th><th>Proposals</th><th>Publications</th><th>Reflections</th><th>Posts</th><th>Words</th><th>Minutes</th><th>Revisions</th><th>Documents</th></tr></thead>
          <tbody>{weeks.map((w) => (
            <tr key={w.week_start}><td>{w.week_start}</td><td>{w.score}</td><td>{w.proposal}</td><td>{w.publication}</td><td>{w.reflections}</td><td>{w.posts}</td>
              <td>{w.words}</td><td>{w.minutes}</td><td>{w.revisions}</td><td>{w.documents}</td></tr>
          ))}</tbody>
        </table>
      </Box>
    </Paper>
  );
}
